import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { supabase } from '@/services/supabase';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

type ResetPasswordFormData = {
  password: string;
  confirmPassword: string;
};

export const ResetPasswordPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  // Get the current password value for confirmation validation
  const password = watch('password', '');

  useEffect(() => {
    // Check for recovery session on component mount
    const checkRecoverySession = async () => {
      try {
        // First try to get the session directly
        const { data: { session } } = await supabase.auth.getSession();
        console.log('Current session state:', session ? 'Session exists' : 'No session');
        
        // If we have a session and it's type is recovery, set recovery mode
        if (session?.user?.email) {
          console.log('User is authenticated, checking for recovery type');
          
          // Check URL hash for recovery token indicators
          const hasRecoveryParams = 
            window.location.hash.includes('type=recovery') || 
            window.location.search.includes('type=recovery');
            
          if (hasRecoveryParams) {
            console.log('Recovery parameters found in URL');
            setIsRecoveryMode(true);
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
      }
    };
    
    // Run the check immediately
    checkRecoverySession();
    
    // Set up auth state change listener to detect PASSWORD_RECOVERY event
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session ? 'with session' : 'without session');
      
      if (event === 'PASSWORD_RECOVERY') {
        console.log('Password recovery mode detected via auth state change');
        setIsRecoveryMode(true);
      }
    });

    // Cleanup listener on component unmount
    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      // Try to get the current session first to ensure we're in a valid state
      const { data: { session } } = await supabase.auth.getSession();
      
      // If we're not in recovery mode but have a session, try to update password anyway
      if (!isRecoveryMode && session?.user) {
        console.warn('Not explicitly in recovery mode but have a session, attempting password update');
        // Force recovery mode if we have a session
        setIsRecoveryMode(true);
      } else if (!isRecoveryMode) {
        console.error('Not in recovery mode and no session found');
        setError('Invalid password reset session. Please request a new password reset link.');
        setIsLoading(false);
        return;
      }

      console.log('Updating password via Supabase');
      
      // Since Supabase has already authenticated the user via the recovery token,
      // we can directly update the password
      const { data: updateData, error } = await supabase.auth.updateUser({
        password: data.password
      });
      
      if (error) {
        throw error;
      }
      
      console.log('Password updated successfully', updateData.user ? 'with user data' : 'without user data');
      
      // Show success message
      setSuccess(true);
      toast.success('Password updated successfully!');
      
      // Clear any stored tokens from localStorage
      localStorage.removeItem('supabase_access_token');
      localStorage.removeItem('supabase_refresh_token');
      localStorage.removeItem('session');
      
      // Sign out to ensure clean state
      await supabase.auth.signOut();
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password');
      toast.error('Failed to reset password: ' + (err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  };

  // If password reset is successful, show confirmation screen
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg">
          <div className="text-center">
            <img
              src="/favicon.png"
              alt="Catch of the Day Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
            <h2 className="text-2xl font-semibold text-gray-800 mb-2">
              Password Reset Complete
            </h2>
            <div className="bg-green-50 text-green-700 p-4 rounded-md mt-4 mb-6 text-sm">
              Your password has been successfully reset. You will be redirected to the login page.
            </div>
            <div className="mt-6">
              <Link
                to="/login"
                className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue transition-colors duration-200"
              >
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg">
        <div className="text-center">
          <img
            src="/favicon.png"
            alt="Catch of the Day Logo"
            className="h-16 w-auto mx-auto mb-4"
          />
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Create New Password
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Please enter your new password below.
          </p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-dark-gray mb-1">
              New Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm pr-10"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                    message: 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character',
                  },
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <FaEyeSlash className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <FaEye className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600 font-medium">{errors.password.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-dark-gray mb-1">
              Confirm New Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 text-dark-gray focus:outline-none focus:ring-2 focus:ring-sky-blue focus:border-sky-blue transition duration-150 ease-in-out sm:text-sm pr-10"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: value => value === password || 'Passwords do not match',
                })}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <FaEyeSlash className="h-5 w-5" aria-hidden="true" />
                ) : (
                  <FaEye className="h-5 w-5" aria-hidden="true" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600 font-medium">{errors.confirmPassword.message}</p>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-100 p-3 rounded-md">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-blue hover:bg-teal focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-blue transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Updating Password...' : 'Reset Password'}
            </button>
          </div>

          <div className="flex items-center justify-center">
            <div className="text-sm">
              <Link to="/login" className="font-medium text-sky-blue hover:text-teal transition-colors duration-200">
                Back to Login
              </Link>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
